import { createTheme, PaletteMode } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';

/**
 * IMC design system tokens.
 * Primary purple is the brand color extracted from the IMC logo mark;
 * secondary blue is the complementary accent used across the logo's
 * wordmark and premium-SaaS-style highlights (links, info states, charts).
 */
export const brand = {
  purple: {
    main: '#5B2DA3',
    light: '#8354C6',
    dark: '#3E1E73',
  },
  blue: {
    main: '#2F6FED',
    light: '#6B9BFA',
    dark: '#1E4FB8',
  },
};

export const brandGradient = 'linear-gradient(135deg, #5B2DA3 0%, #3E1E73 55%, #2F6FED 100%)';

const softShadow = '0 2px 8px rgba(17, 12, 34, 0.06), 0 1px 2px rgba(17, 12, 34, 0.04)';
const softShadowDark = '0 2px 8px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)';

export const getTheme = (mode: PaletteMode) => {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: { main: brand.purple.main, light: brand.purple.light, dark: brand.purple.dark, contrastText: '#fff' },
      secondary: { main: brand.blue.main, light: brand.blue.light, dark: brand.blue.dark, contrastText: '#fff' },
      ...(isLight
        ? {
            background: { default: '#f6f5fa', paper: '#ffffff' },
            divider: 'rgba(91, 45, 163, 0.10)',
          }
        : {
            background: { default: '#100f17', paper: '#191722' },
            divider: 'rgba(255, 255, 255, 0.08)',
          }),
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'Roboto',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700, letterSpacing: -0.5 },
      h5: { fontWeight: 600, letterSpacing: -0.25 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 18 },
          sizeLarge: { paddingBlock: 12 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 16 },
          outlined: { borderColor: isLight ? 'rgba(91, 45, 163, 0.12)' : 'rgba(255, 255, 255, 0.08)' },
          elevation1: { boxShadow: isLight ? softShadow : softShadowDark },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isLight ? softShadow : softShadowDark,
            border: `1px solid ${isLight ? 'rgba(91, 45, 163, 0.08)' : 'rgba(255, 255, 255, 0.06)'}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20 },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${isLight ? 'rgba(91, 45, 163, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 700,
              backgroundColor: isLight ? '#f6f5fa' : '#1c1a26',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontWeight: 500 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '--DataGrid-rowBorderColor': isLight ? 'rgba(91, 45, 163, 0.08)' : 'rgba(255, 255, 255, 0.08)',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isLight ? '#f6f5fa' : '#1c1a26',
              borderRadius: 0,
            },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: isLight ? 'rgba(91, 45, 163, 0.05)' : 'rgba(255, 255, 255, 0.05)',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: isLight ? 'rgba(91, 45, 163, 0.10)' : 'rgba(139, 92, 246, 0.16)',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: isLight ? 'rgba(91, 45, 163, 0.14)' : 'rgba(139, 92, 246, 0.22)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${isLight ? 'rgba(91, 45, 163, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          },
        },
      },
    },
  });
};
